// Gestione allegati ticket con upload e download
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Download, Trash2, File, Image, FileText } from 'lucide-react'
import { FileUpload } from '../../../components/shared/FileUpload'
import { Button } from '../../../components/ui/Button'
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog'
import api from '../../../lib/api'

interface Attachment {
  key: string
  name: string
  mimeType: string
  size: number
  url: string
  uploadedAt: string
}

interface TicketAttachmentsProps {
  ticketId: string
}

export function TicketAttachments({ ticketId }: TicketAttachmentsProps) {
  const qc = useQueryClient()
  const [files, setFiles] = useState<Array<{ file: File; preview?: string }>>([])
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)

  const { data: attachments = [] } = useQuery({
    queryKey: ['ticket-attachments', ticketId],
    queryFn: async () => {
      try {
        const res = await api.get<Attachment[]>(`/tickets/${ticketId}/attachments`)
        return res.data ?? []
      } catch {
        return []
      }
    },
  })

  const uploadMutation = useMutation({
    mutationFn: async () => {
      const formData = new FormData()
      files.forEach(({ file }) => formData.append('files', file))
      await api.post(`/tickets/${ticketId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    },
    onSuccess: () => {
      setFiles([])
      void qc.invalidateQueries({ queryKey: ['ticket-attachments', ticketId] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (key: string) => {
      await api.delete(`/tickets/${ticketId}/attachments/${encodeURIComponent(key)}`)
    },
    onSuccess: () => {
      setDeleteTarget(null)
      void qc.invalidateQueries({ queryKey: ['ticket-attachments', ticketId] })
    },
  })

  const getIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <Image className="h-5 w-5" />
    if (mimeType === 'application/pdf') return <FileText className="h-5 w-5" />
    return <File className="h-5 w-5" />
  }

  return (
    <div className="space-y-4">
      {attachments.length > 0 && (
        <ul className="space-y-2" role="list" aria-label="Allegati esistenti">
          {attachments.map((att) => (
            <li
              key={att.key}
              className="flex items-center gap-3 rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface-raised)] px-3 py-2"
            >
              <span className="text-[var(--fc-foreground-muted)]" aria-hidden="true">
                {getIcon(att.mimeType)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-[var(--fc-foreground)]">{att.name}</p>
                <p className="text-xs text-[var(--fc-foreground-muted)]">
                  {(att.size / 1024).toFixed(0)} KB · {new Date(att.uploadedAt).toLocaleDateString('it-IT')}
                </p>
              </div>
              <div className="flex gap-1">
                <a
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={att.name}
                  className="flex h-8 w-8 items-center justify-center rounded text-[var(--fc-foreground-muted)] hover:bg-[var(--fc-surface-overlay)] hover:text-[var(--fc-foreground)] transition-colors"
                  aria-label={`Scarica ${att.name}`}
                >
                  <Download className="h-4 w-4" />
                </a>
                <button
                  onClick={() => setDeleteTarget(att.key)}
                  className="flex h-8 w-8 items-center justify-center rounded text-[var(--fc-foreground-muted)] hover:bg-[var(--fc-surface-overlay)] hover:text-[var(--fc-danger)] transition-colors"
                  aria-label={`Elimina ${att.name}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {attachments.length === 0 && files.length === 0 && (
        <p className="text-center text-sm text-[var(--fc-foreground-muted)] py-4">
          Nessun allegato. Carica documenti, foto o file relativi all'intervento.
        </p>
      )}

      <FileUpload
        files={files}
        onAdd={(newFiles) => setFiles((prev) => [...prev, ...newFiles])}
        onRemove={(index) => setFiles((prev) => prev.filter((_, i) => i !== index))}
      />

      {files.length > 0 && (
        <Button
          onClick={() => uploadMutation.mutate()}
          isLoading={uploadMutation.isPending}
          className="w-full"
        >
          Carica {files.length} {files.length === 1 ? 'file' : 'file'}
        </Button>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
        title="Elimina allegato"
        message="Sei sicuro di voler eliminare questo allegato? L'operazione non è reversibile."
        confirmLabel="Elimina"
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}
